import { useParams, useNavigate, Link } from 'react-router-dom'
import { getArticleById } from '@/core/data/wikiArticlesIndex'
import type { WikiSection, WikiTip, WikiTable, WikiStep } from '@/core/data/wikiTypes'
import { ChevronLeft, ChevronRight, BookOpen, Lightbulb, AlertTriangle, Info, Sparkles, Camera } from 'lucide-react'
import { cn } from '@/shared/lib/utils'

/* eslint-disable @typescript-eslint/no-explicit-any */

// ── Tip Block ────────────────────────────────────────────────
function TipBlock({ tip }: { tip: WikiTip }) {
  const config = {
    tip: { icon: Lightbulb, bg: 'bg-gold-light', border: 'border-gold/30', iconColor: 'text-gold-dark', label: 'Conseil' },
    warning: { icon: AlertTriangle, bg: 'bg-brick-light', border: 'border-brick/30', iconColor: 'text-brick', label: 'Attention' },
    info: { icon: Info, bg: 'bg-teal-light', border: 'border-teal/30', iconColor: 'text-teal', label: 'Info' },
    example: { icon: Sparkles, bg: 'bg-paper-warm', border: 'border-ink-border', iconColor: 'text-gold-dark', label: 'Exemple' },
  }[tip.type]

  return (
    <div className={cn('rounded-lg border p-4 my-4', config.bg, config.border)}>
      <div className="flex items-start gap-3">
        <config.icon size={16} className={cn('flex-shrink-0 mt-0.5', config.iconColor)} />
        <div>
          <p className="text-[10px] font-bold tracking-[0.1em] uppercase mb-1" style={{ color: 'inherit' }}>{config.label}</p>
          <p className="text-[13px] leading-relaxed text-ink/80">{tip.content}</p>
        </div>
      </div>
    </div>
  )
}

// ── Table Block ──────────────────────────────────────────────
function TableBlock({ table }: { table: WikiTable }) {
  return (
    <div className="overflow-x-auto my-4 rounded-lg border border-ink-border">
      <table className="w-full text-[13px]">
        <thead>
          <tr className="bg-onyx text-paper">
            {table.headers.map((h, i) => (
              <th key={i} className="text-left px-4 py-2.5 text-[9px] font-medium tracking-[0.12em] uppercase text-paper/60">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((row, i) => (
            <tr key={i} className={cn('border-t border-ink-border/20', i % 2 === 1 && 'bg-paper')}>
              {row.map((cell, j) => (
                <td key={j} className={cn('px-4 py-2.5', j === 0 ? 'font-medium text-ink' : 'text-ink-muted')}>
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ── Steps Block ──────────────────────────────────────────────
function StepsBlock({ steps }: { steps: WikiStep[] }) {
  return (
    <div className="my-4 space-y-0 border-l-2 border-gold/30 ml-3">
      {steps.map((step) => (
        <div key={step.number} className="relative pl-8 pb-4 last:pb-0">
          {/* Step number dot */}
          <div className="absolute left-[-11px] top-0.5 w-5 h-5 rounded-full bg-gold text-onyx text-[10px] font-bold flex items-center justify-center">
            {step.number}
          </div>
          <div>
            <p className="text-[13px] font-semibold text-ink">{step.title}</p>
            <p className="text-[12px] font-light text-ink-muted mt-0.5 leading-relaxed">{step.description}</p>
          </div>
        </div>
      ))}
    </div>
  )
}

// ── Screenshot Hint ──────────────────────────────────────────
function ScreenshotHint({ hint }: { hint: string }) {
  return (
    <div className="my-4 rounded-lg border border-dashed border-gold/30 bg-gold-light/30 px-4 py-3 flex items-center gap-3">
      <Camera size={16} className="text-gold/60 flex-shrink-0" />
      <p className="text-[11px] text-gold-dark/70 italic">{hint}</p>
    </div>
  )
}

// ── Section Block ────────────────────────────────────────────
function SectionBlock({ section, index }: { section: WikiSection; index: number }) {
  // Parse simple markdown bold **text**
  const renderContent = (text: string) => {
    return text.split('\n\n').map((paragraph, i) => {
      // Handle bullet points
      if (paragraph.includes('\n•') || paragraph.startsWith('•')) {
        const lines = paragraph.split('\n').filter(Boolean)
        return (
          <div key={i} className="my-2">
            {lines.map((line, j) => {
              if (line.startsWith('•')) {
                return (
                  <div key={j} className="flex items-start gap-2 py-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-gold mt-1.5 flex-shrink-0" />
                    <span className="text-[13px] text-ink/80 leading-relaxed">{line.replace('• ', '')}</span>
                  </div>
                )
              }
              return <p key={j} className="text-[13px] text-ink/80 leading-relaxed mb-2">{line}</p>
            })}
          </div>
        )
      }
      // Handle bold **text**
      const parts = paragraph.split(/\*\*(.*?)\*\*/g)
      return (
        <p key={i} className="text-[13px] text-ink/80 leading-relaxed mb-3">
          {parts.map((part, j) => j % 2 === 1 ? <strong key={j} className="font-semibold text-ink">{part}</strong> : part)}
        </p>
      )
    })
  }

  return (
    <section className="mb-8" id={`section-${index}`}>
      <h2 className="font-serif text-[17px] font-bold text-ink tracking-tight mb-3 flex items-center gap-2">
        <span className="w-5 h-5 rounded bg-gold/10 text-gold-dark text-[10px] font-bold flex items-center justify-center flex-shrink-0">
          {index + 1}
        </span>
        {section.title}
      </h2>

      {renderContent(section.content)}
      {section.table && <TableBlock table={section.table} />}
      {section.steps && <StepsBlock steps={section.steps} />}
      {section.tip && <TipBlock tip={section.tip} />}
      {section.screenshotHint && <ScreenshotHint hint={section.screenshotHint} />}
    </section>
  )
}

// ══════════════════════════════════════════════════════════════
//  MAIN PAGE
// ══════════════════════════════════════════════════════════════

export function WikiArticlePage() {
  const { articleId } = useParams<{ articleId: string }>()
  const navigate = useNavigate()
  const article = articleId ? getArticleById(articleId) : undefined

  if (!article) {
    return (
      <div className="text-center py-20">
        <BookOpen size={48} className="mx-auto text-gold/30 mb-4" />
        <h1 className="font-serif text-xl text-ink mb-2">Article non trouvé</h1>
        <p className="text-[13px] text-ink-muted mb-6">Ce guide n'existe pas encore.</p>
        <Link to="/wiki" className="text-[13px] font-medium text-brick hover:text-brick-dark">← Retour au wiki</Link>
      </div>
    )
  }

  return (
    <div className="max-w-3xl mx-auto">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-[11px] text-ink-faint mb-6">
        <Link to="/wiki" className="hover:text-ink transition-colors">TremorWiki</Link>
        <span>/</span>
        <Link to={`/wiki?cat=${article.categoryId}`} className="hover:text-ink transition-colors">{article.categoryLabel}</Link>
        <span>/</span>
        <span className="text-ink-muted">Guide {article.guideNumber}/{article.totalGuides}</span>
      </div>

      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-[9px] font-bold tracking-[0.2em] uppercase px-2 py-0.5 rounded-full"
            style={{ background: `${article.categoryColor}15`, color: article.categoryColor }}>
            {article.categoryLabel}
          </span>
          <span className="text-[10px] text-ink-faint">· {article.readTime} de lecture</span>
        </div>

        <h1 className="font-serif text-[28px] font-bold text-ink tracking-[-0.02em] leading-tight mb-2">
          {article.title}
        </h1>
        <p className="text-[14px] font-light text-ink-muted leading-relaxed">
          {article.subtitle}
        </p>
        <div className="divider-gold mt-4" />
      </div>

      {/* Table of contents */}
      {article.sections.length > 3 && (
        <div className="bg-white rounded-card border border-ink-border p-5 mb-8 shadow-card">
          <h3 className="text-[10px] font-bold tracking-[0.12em] uppercase text-ink-faint mb-3">Sommaire</h3>
          <div className="space-y-1.5">
            {article.sections.map((s, i) => (
              <a key={i} href={`#section-${i}`}
                className="flex items-center gap-2.5 text-[12px] text-ink-muted hover:text-ink transition-colors py-0.5">
                <span className="w-4 h-4 rounded bg-gold/10 text-gold-dark text-[9px] font-bold flex items-center justify-center flex-shrink-0">
                  {i + 1}
                </span>
                {s.title}
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Content */}
      <div>
        {article.sections.map((section, i) => (
          <SectionBlock key={i} section={section} index={i} />
        ))}
      </div>

      {/* Navigation prev/next */}
      <div className="flex items-center justify-between mt-12 pt-6 border-t border-ink-border">
        {article.prevArticle ? (
          <button onClick={() => navigate(`/wiki/${article.prevArticle}`)}
            className="flex items-center gap-2 text-[13px] text-ink-muted hover:text-ink transition-colors group">
            <ChevronLeft size={16} className="group-hover:-translate-x-0.5 transition-transform" />
            <span>Guide précédent</span>
          </button>
        ) : <div />}

        <Link to="/wiki" className="text-[12px] text-ink-faint hover:text-ink transition-colors">
          ← Tous les guides
        </Link>

        {article.nextArticle ? (
          <button onClick={() => navigate(`/wiki/${article.nextArticle}`)}
            className="flex items-center gap-2 text-[13px] text-ink-muted hover:text-ink transition-colors group">
            <span>Guide suivant</span>
            <ChevronRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
          </button>
        ) : <div />}
      </div>
    </div>
  )
}
